import * as zod from 'zod/v4';

/** Represents an error thrown by the fetchJson utility when an HTTP request fails. */
class FetchError extends Error {
    /** Set it to "FetchError" to identify the error type */
    name = "FetchError";
    /** The HTTP status code returned by the failed request */
    status;
    /** The error body from the server, if available */
    errorBody;
    constructor({ status, errorBody }) {
        super(`Request failed with status ${status}`);
        this.status = status;
        this.errorBody = errorBody;
    }
}

/** A wrapper for fetch that expects a JSON and throws a typed error if fails */
async function fetchJson({ url, options, }) {
    const { method, headers = {}, body, credentials = "include" } = options;
    const finalHeaders = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "Content-Type": "application/json",
        ...headers,
    };
    const response = await fetch(url, {
        method,
        headers: finalHeaders,
        body: body ? JSON.stringify(body) : undefined,
        credentials,
    });
    if (!response.ok) {
        const contentType = response.headers.get("content-type") || "";
        let errorBody;
        try {
            errorBody = contentType.includes("application/json")
                ? await response.json()
                : await response.text();
        }
        catch {
            errorBody = null;
        }
        throw new FetchError({ status: response.status, errorBody });
    }
    return response.json();
}

/**
 * Reusable abstraction wrapping the logic to communicating with core api
 * Allows to make type-safe GET and POST requests
 */
class BaseApiClient {
    baseUrl;
    headers;
    onLogAssertion;
    urls = {
        usersV3: "/v3/users",
        dashboardV3: "/v3/dashboard",
    };
    constructor({ baseUrl, headers, onLogAssertion }) {
        this.baseUrl = baseUrl;
        this.headers = headers ?? {};
        this.onLogAssertion = onLogAssertion;
    }
    /** Sends a GET request and return the data property */
    async get(path) {
        const response = await fetchJson({
            url: `${this.baseUrl}${path}`,
            options: {
                method: "GET",
                headers: this.headers,
            },
        });
        return response.data;
    }
    /** Sends a POST request and return the data property */
    async post(path, body) {
        const response = await fetchJson({
            url: `${this.baseUrl}${path}`,
            options: {
                method: "POST",
                headers: this.headers,
                body,
            },
        });
        return response.data;
    }
    /** Sends a PUT request and return the data property */
    async put(path, body) {
        const response = await fetchJson({
            url: `${this.baseUrl}${path}`,
            options: {
                method: "PUT",
                headers: this.headers,
                body,
            },
        });
        return response.data;
    }
    /**
     * Validates an object against a Zod schema.
     * @param obj The object to validate against the schema.
     * @param schema The Zod schema to validate against.
     * @param routeName Optional route name for logging purposes.
     * @returns The same object, no modifications are made.
     */
    assertSchema(obj, schema, routeName) {
        const result = schema.safeParse(obj);
        if (!result.success && this.onLogAssertion) {
            const errorMessage = `Assertion failed for route "${routeName}": ${result.error.issues
                .map((e) => e.message)
                .join(", ")}`;
            this.onLogAssertion(errorMessage, result.error);
        }
        return result.success ? result.data : obj;
    }
}

/** Whether the user can create groups. Usually it is only possible to grant it to group managers. */
const createGroupPermissionSchema = zod.literal("createGrp");
/** Special permissions that can be granted to users. */
const userPermissionNameSchema = createGroupPermissionSchema;
/** Represents a single permission that can be granted to a user. */
const userPermissionSchema = zod.object({
    /** Name of the permission to either allow or deny. */
    name: userPermissionNameSchema,
    /**
     * Indicates whether the permission is allowed for the user.
     * If true, the user has the permission; if false, they do not.
     */
    allowed: zod.boolean(),
});

/** Role of user in workspace */
const workspaceRoleSchema = zod.enum([
    "Administrator",
    "owner",
    "Enterprise Admin",
    "Enterprise Viewer",
    "Company Admin",
    "Group Manager",
    "Project Manager",
    "Company Member",
]);
const userCompanyRoleSchema = zod.enum([
    /** Equal to enterprise admins. Have the highest role in company */
    "COMPANY_EXECUTIVE",
    /** Equal to Enterprise viewer */
    "COMPANY_VIEWER",
    /** Equal to group manager */
    "COMPANY_MANAGER",
    /**
     * A group member role with ability to view the group's details, setting, members and all projects,
     * including their contents and all projects of the group (including contents).
     */
    "GROUP_VIEWER",
    /** Project manager. In project view (EUserProjectRole) it is equal to project owner */
    "PROJECT_MANAGER",
    /**
     * Every role in company is also a member of it. This is the default role for every user in a company
     * If a new member is invited as a project viewer of a project, they become member of company
     * If that member is removed from project (as viewer), they still remain as member of company
     */
    "MEMBER",
]);
/** Role of user in a project */
const userProjectRoleSchema = zod.enum([
    "RESOURCE_OWNER",
    "RESOURCE_ADMIN",
    "RESOURCE_EDITOR",
    "RESOURCE_VIEWER",
]);

/** User ID */
const userIdSchema = zod.string();
/** Kind of users whether a user or a team */
const kindSchema = zod.enum(["user", "user-group"]);
/** User entity, contains all the information about the user */
const userSchema = zod.object({
    /**
     * The id of the user
     * Would be the same as identity if user is registered
     */
    id: userIdSchema.optional(),
    /**
     * The email of the user
     * Would be the same as identity if user is not registered
     */
    email: zod.string(),
    /**
     * equal to email if user is invited to company, but not registered
     * equal to id if user is registered
     */
    identity: userIdSchema,
    /** Name might be returned instead of firstName + lastName */
    name: zod.string().optional(),
    /** If the name of the user is not combined in name, it is split between first and last name */
    firstName: zod.string().optional(),
    /** If the name of the user is not combined in name, it is split between first and last name */
    lastName: zod.string().optional(),
    /** URL to the user's thumbnail image, can be an empty string if not available */
    thumbnailUrl: zod.string().optional(),
    /**
     * Specifies the type of the entity: either an individual user or a team.
     *
     * - `"user"`: Represents an individual user.
     * - `"user-group"`: Represents a team.
     */
    kind: kindSchema.optional(),
    /**
     * Array filled with permissions that the user has.
     * Each permission is represented by a name and a boolean indicating if it is allowed.
     */
    permissions: zod.array(userPermissionSchema),
});
/** User schema if the user is returned as a manager in company or project level */
const userAsManagerSchema = userSchema.omit({ permissions: true });
/** Base company member entity */
const companyMemberBaseSchema = userAsManagerSchema.extend({
    /**
     * The effective role of the member, including transitive assignments via user groups, meaning that
     * the highest role of the user in company or user groups will be returned in here
     */
    role: userCompanyRoleSchema,
    /** The permissions that the member has. If a company permission is missing, its same as if its disallowed */
    permissions: zod.array(userPermissionSchema).optional(),
});
/** Response received from server when requesting members of a company */
const companyMemberDetailsSchema = companyMemberBaseSchema.extend({
    /** The role directly assigned to the user in the company */
    userRole: userCompanyRoleSchema,
});
/**
 * User interface if the user is returned as a member in company or project level
 * The only different with manager is that they have a role prop
 */
const projectMemberBaseSchema = userAsManagerSchema.extend({
    /** The role of member in project */
    role: userProjectRoleSchema,
});

/** All the APIs for fetching and updating companies */
class CompanyClient extends BaseApiClient {
    /** Get all the members in a company */
    async getMembersInCompany({ companyId, types, }) {
        const baseUrl = `${this.urls.dashboardV3}/company/${companyId}/member`;
        const query = types?.length
            ? `?${new URLSearchParams({ types: types.join(",") })}`
            : "";
        const url = `${baseUrl}${query}`;
        const response = await this.get(url);
        return this.assertSchema(response, zod.array(companyMemberDetailsSchema), "getMembersInCompany");
    }
    /** Get a specific member of a company */
    async getMemberDetailsInCompany({ companyId, identity, }) {
        const response = await this.get(`${this.urls.dashboardV3}/company/${companyId}/member/${encodeURIComponent(identity)}`);
        return this.assertSchema(response, companyMemberDetailsSchema, "getMemberDetailsInCompany");
    }
    /** Updates a member in a company */
    async updateCompanyMember({ companyId, identity, payload, }) {
        const response = await this.put(`${this.urls.dashboardV3}/company/${companyId}/member/${encodeURIComponent(identity)}`, payload);
        return this.assertSchema(response, companyMemberDetailsSchema, "updateCompanyMember");
    }
}

/** Data that is returned when fetching for the user's hash in Intercom */
const intercomDataSchema = zod.object({
    /** The Id of the logged in user */
    userId: userIdSchema,
    /** Hash computed by the backend based on the userId and the secret key */
    hash: zod.string(),
});

/** All available workspace types */
const workspaceTypeSchema = zod.enum([
    /** A workspace connected to a dashboard */
    "company",
    /** Workspaces only accessible to HoloBuilder admins. Providing access to admin services (e.g. the admin panel) */
    "admin",
]);
/** A workspace containing the api, icon etc. */
const workspaceSchema = zod.object({
    /** The apiUrl of the workspace, i.e. where to send (login) requests */
    apiUrl: zod.string(),
    /** This is for companies that have a Holobuilder subdomain and otherwise will be undefined */
    domain: zod.string().optional(),
    /** Icon to show with the workspace, e.g. Company Logo for Dashboards. Can be an empty string if not available */
    icon: zod.string().optional(),
    /** Name of the workspace */
    name: zod.string(),
    /** The highest role the user has in the workspace */
    role: workspaceRoleSchema,
    /** The type of the workspace, can be either a normal (company workspace) or the admin panel */
    type: workspaceTypeSchema,
    /** Url to workspace, e.g. dashboard.xxx/workspace-id */
    url: zod.string(),
});

/** Defines the schema for the GetCurrentUsersWorkspaces response */
const getCurrentUsersWorkspacesSchema = zod.object({
    /** List of workspaces */
    workspaces: zod.array(workspaceSchema),
});

/** All the APIs for fetching and updating users */
class UsersClient extends BaseApiClient {
    /**
     * Gets an object representing all workspaces that the current user belongs to.
     * Can return undefined if the user is not logged in.
     */
    async getCurrentUsersWorkspaces() {
        const response = await this.get(`${this.urls.usersV3}/me/workspaces`);
        return this.assertSchema(response, getCurrentUsersWorkspacesSchema, "getCurrentUsersWorkspaces");
    }
    /** Get profile information for the current user */
    async getCurrentUserProfileInfos(companyId) {
        const response = await this.get(`${this.urls.dashboardV3}/company/${companyId}/me`);
        return this.assertSchema(response, companyMemberBaseSchema, "getCurrentUserProfileInfos");
    }
    /** Gets both the hash and the user id to identify users in Intercom. */
    async getIntercomHash() {
        const response = await this.get(`${this.urls.usersV3}/intercom`);
        return this.assertSchema(response, intercomDataSchema, "getIntercomHash");
    }
}

/** Client for interacting with the Core API. */
class CoreApiClient {
    users;
    company;
    constructor({ baseUrl, headers, onLogAssertion }) {
        this.users = new UsersClient({ baseUrl, headers, onLogAssertion });
        this.company = new CompanyClient({ baseUrl, headers, onLogAssertion });
    }
}

var index$1 = /*#__PURE__*/Object.freeze({
    __proto__: null
});

// TODO: This will replace to string only after all routes are updated to use companyId from CoreApiClient: https://faro01.atlassian.net/browse/ST-3223
/** Company ID */
const companyIdSchema = zod.union([zod.string(), zod.number()]);

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    companyIdSchema: companyIdSchema,
    companyMemberBaseSchema: companyMemberBaseSchema,
    companyMemberDetailsSchema: companyMemberDetailsSchema,
    createGroupPermissionSchema: createGroupPermissionSchema,
    getCurrentUsersWorkspacesSchema: getCurrentUsersWorkspacesSchema,
    intercomDataSchema: intercomDataSchema,
    kindSchema: kindSchema,
    projectMemberBaseSchema: projectMemberBaseSchema,
    userAsManagerSchema: userAsManagerSchema,
    userCompanyRoleSchema: userCompanyRoleSchema,
    userIdSchema: userIdSchema,
    userPermissionNameSchema: userPermissionNameSchema,
    userPermissionSchema: userPermissionSchema,
    userProjectRoleSchema: userProjectRoleSchema,
    userSchema: userSchema,
    workspaceRoleSchema: workspaceRoleSchema,
    workspaceSchema: workspaceSchema,
    workspaceTypeSchema: workspaceTypeSchema
});

export { CoreApiClient, index as FaroCoreApiSchemas, index$1 as FaroCoreApiTypes };
